import {palette} from 'Shared/artists/Palette';
import {For,createSignal,createMemo, Show} from 'solid-js';
import { css } from '@emotion/css';
import { StoreProvider, useStore } from 'Shared/StoreProvider';
import { dbPatchField } from 'Shared/forms/BaseOperations';
import { getValue, id, Integer, integerStyle, selectStyle } from 'Shared/forms/Inputs';
import { labelStyle } from 'Shared/forms/Wrap';
import { sizes } from 'Shared/backend/Theme';

/*
	I've removed vw for the moment as it mucks up on screens that exceed the max page width.
	In general we can use the 'aspect-ratio' CSS property to achieve a similar effect.

	I've removed % as there are generally better options (esp 'use parent height').

	I've removed em as resizing everthing based on font-size is confusing and likely to be 
	difficult to maintain.

	Added 'full width' for overlay foregrounds (only). Needed for hover-style overlays. Note that
	other components are 100% width by default.
*/

export const defaultAmountUnits = {px:'px',vh:'vh',fluid:'fluid'};

const amountStyle = () => css({
	display: 'flex'
});

type FluidDefaults = {pageMin:number; minValue:number; pageMax:number; maxValue:number};

export function Amount(P:IInteger & {options:Record<string,string>; fluidDefaults?:FluidDefaults})
{
	const fluidDefaults = P.fluidDefaults ?? {pageMin:sizes.endFontShrink,minValue:200,pageMax:sizes.minDesktopWidth,maxValue:500};

	const sw = useStore();
	const [error,setError] = createSignal(undefined as undefined|string);

	const onChange = async  e => {
		const parent = e.target.parentNode;

		const unit = parent.querySelector('select').value;

		if (unit=='fluid') {
			await dbPatchField(sw,P.field,{type:'fluid', ...fluidDefaults});
			return;
		}

		if (unit=='full' || unit=='children') {
			await dbPatchField(sw,P.field,unit);
			return;
		}

		const input = parent.querySelector('input');
		if (!input) {
			/* Presumably was 'fluid' */
			await dbPatchField(sw,P.field,undefined);
			return;
		}

		const num = input.value.trim();
		if (num=='')
			return undefined;
		let value = num + unit;

		/* This callback is limited in its scope due to the complexity of the field. Could possibly do better */
		if (P.processInput)
			value = P.processInput(value);

		await dbPatchField(sw,P.field,value);
	};

	const pair = createMemo(() => {
		const value = getValue(sw.location,P.field);

		if (!value)
			return {amount:undefined,unit:undefined};
		if (value=='full')
			return {unit: 'full'};
		if (value.type=='fluid' || value.type=='full' || value.type=='children')
			return {unit: value.type};

		return {
			amount: (''+value).match(/[0-9\.]+/)?.[0],    //XXX ?. hack
			unit: (''+value).match(/[0-9\.]+(.*)/)?.[1]   //XXX ?. hack
		};
	});

//TODO check my existing values + allow to set	

	return (<>		
		<div class={css(amountStyle(),P.class)}>
			<select name='' 
				class={selectStyle()}
				autocomplete='off' 
				disabled={P.disable}
				onChange={onChange}
				style={error() ? 'background-color:'+palette.errorBackground : ''}
			>
				<For each={Object.entries(P.options)}>{ item => 
					<option value={item[0]} selected={item[0]==pair().unit}>
						{item[1]}
					</option>
				}</For>
			</select>

			<Show when={pair().unit!='fluid' && pair().unit!='children' && pair().unit!='full'}>
				{' '}
				<input type='number' id={id(P.field)} name=''
					class={css(integerStyle(false))}
					value={pair().amount}
					max={P.max} min={P.min} step={P.step}
					onChange={onChange}
					disabled={P.disable}
					placeholder={P.placeholder ?? '(none)'}
				/>
			</Show>
		</div>

		<Show when={pair().unit == 'fluid'}>
			<StoreProvider {...sw} location={[...sw.location,P.field]}>
				<Fluid />
			</StoreProvider>
		</Show>
	</>);
}


const fluidInputStyle = () => css(integerStyle(false), {
	width: '4em',
	textAlign: 'right'
});

function Fluid()
{
	return (
		<div>
			<label class={labelStyle()}>Min (px)</label>
			<Integer class={fluidInputStyle()} field='minValue' />
			<span class={labelStyle()}>@</span>
			<Integer class={fluidInputStyle()} field='pageMin' />
			<span class={labelStyle()}>wide</span>

			&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;

			<label class={labelStyle()}>Max (px)</label>
			<Integer class={fluidInputStyle()} field='maxValue' />
			<span class={labelStyle()}>@</span>
			<Integer class={fluidInputStyle()} field='pageMax' />
			<span class={labelStyle()}>wide</span>
		</div>
	);
}


